/**
 * The per-row actions: Deploy (or Redeploy anyway), Compile only, and Acknowledge
 * skip — or, on a row that has been skipped, Cancel skip and nothing else. Four
 * things about them are deliberate.
 *
 * **Every action that is not plainly the next thing to do asks first.** Deploying out
 * of the registry's group order, redeploying something already at this commit, and
 * deploying a component the release does not change are all legitimate — the order is
 * advice and a no-change redeploy is a real technique — but each is a choice, and the
 * dialog is where the reason for it is stated. Acknowledging a skip *requires* a
 * reason, because that one outlives the release: it is what the record shows for a
 * changed component production never got.
 *
 * **The button label follows the state.** "Deploy" on something already deployed reads
 * as "this did not take"; the spec asks for "Redeploy anyway" there.
 *
 * **A skipped row offers only the undo.** Leaving Deploy and Compile only beside a
 * skip read as if the skip had not taken; the row now says one thing, and deploying
 * it after all is one Cancel skip away.
 *
 * **Availability comes from the shared legality table**, not from a local rule, so the
 * UI cannot offer what the host would reject.
 */
import { useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    TextField,
    Tooltip,
} from '@mui/material';
import { INSTRUCTION_KIND, legalInstructions, type HostMachine, type InstructionKind, type Phase } from '@finvari/deployer';
import { compileOnlyExplanation, componentSlotsReason, isBusy, orderWarning, type ComponentRow } from '../lib/componentRows';
import { INFLIGHT_DISABLED_REASON } from '../lib/instructionActivity';
import { createInstruction } from '../lib/createInstruction';
import { dbForRole } from '../firebase';
import { useOperator } from '../auth';

/** What a confirmation dialog is being opened for. */
type Confirming = 'deploy' | 'compile' | 'acknowledge';

export function ComponentActions({
    row,
    rows,
    version,
    phase,
    machine,
}: {
    row: ComponentRow;
    /** Every row, for the out-of-order check and the machine's build limit. */
    rows: ComponentRow[];
    version: string;
    phase: Phase;
    /** The release host's hardware, which bounds how many components run at once. */
    machine?: HostMachine;
}) {
    const operator = useOperator();
    const [confirming, setConfirming] = useState<Confirming | undefined>(undefined);
    const [reason, setReason] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);

    const busyReason = isBusy(row) ? INFLIGHT_DISABLED_REASON : undefined;
    // The machine's limit applies only to starting *another* build; a row that is
    // already running is unaffected, and so is Acknowledge skip, which spends
    // nothing. The host enforces the same limit — this is so the operator is not
    // offered a button whose press comes back rejected.
    const slotsReason = componentSlotsReason(rows, machine);
    const deployReason = reasonFor(phase, INSTRUCTION_KIND.deployComponent) ?? busyReason ?? slotsReason;
    const compileReason = reasonFor(phase, INSTRUCTION_KIND.deployCompile) ?? busyReason ?? slotsReason;
    const acknowledgeReason =
        reasonFor(phase, INSTRUCTION_KIND.deployAcknowledgeSkip) ??
        busyReason ??
        (row.changeKind === 'none'
            ? 'Nothing to acknowledge — this release changes nothing in this component.'
            : row.changeKind === 'compile-only'
              ? 'Nothing to acknowledge — this component does not hold the release open.'
              : row.state === 'deployed'
                ? 'Already deployed, so there is no skip to account for.'
                : undefined);

    const order = orderWarning(row, rows);
    const deployed = row.state === 'deployed';
    // An earlier release put this commit in production. The button is the same
    // Redeploy anyway either way; only the dialog says which of the two it is.
    const upToDate = row.state === 'upToDate';
    const atThisCommit = deployed || upToDate;
    const unchanged = row.changeKind === 'none';
    const compileOnly = row.state === 'compileOnly';

    const run = async (kind: InstructionKind, params: Record<string, unknown>) => {
        setSending(true);
        setError(undefined);
        const result = await createInstruction(dbForRole('release'), 'release', kind, params, operator, version);
        setSending(false);
        if (!result.ok) {
            setError(result.error);
            return;
        }
        setConfirming(undefined);
        setReason('');
    };

    const deploy = () => void run(INSTRUCTION_KIND.deployComponent, { component: row.name });
    const compile = () => void run(INSTRUCTION_KIND.deployCompile, { component: row.name });

    // Deploying the next component in order, with changes, needs no conversation.
    const deployNeedsConfirming = !!order || atThisCommit || unchanged || compileOnly;

    if (row.state === 'acknowledged') {
        const cancelReason = reasonFor(phase, INSTRUCTION_KIND.deployCancelSkip) ?? busyReason;
        return (
            <>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Tooltip
                        title={
                            cancelReason ??
                            `Puts ${row.name} back to not started, and removes the recorded reason`
                        }
                    >
                        <span>
                            {/*
                              * No dialog: it undoes a record the operator just made, the
                              * reason is on screen beside the button, and recording the
                              * skip again costs one dialog.
                              */}
                            <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                disabled={!!cancelReason || sending}
                                onClick={() => void run(INSTRUCTION_KIND.deployCancelSkip, { component: row.name })}
                            >
                                Cancel skip
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>
                {error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                        {error}
                    </Alert>
                )}
            </>
        );
    }

    return (
        <>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
                {row.compilable && (
                    <Tooltip
                        title={
                            compileReason ??
                            (compileOnly
                                ? 'The only thing this component needs from this release'
                                : 'Compile without deploying — checks the build before committing to a deploy')
                        }
                    >
                        <span>
                            {/*
                              * Promoted to the primary action on a compile-only row: the
                              * compile is what the release actually needs there, and
                              * leaving Deploy looking primary would undo the point.
                              */}
                            <Button
                                size="small"
                                variant={compileOnly ? 'contained' : 'text'}
                                disabled={!!compileReason || sending}
                                onClick={() => (row.compiled ? setConfirming('compile') : compile())}
                            >
                                Compile only
                            </Button>
                        </span>
                    </Tooltip>
                )}
                {row.changeKind !== 'none' && row.changeKind !== 'compile-only' && row.state !== 'deployed' && (
                    <Tooltip title={acknowledgeReason ?? 'Record a reason for shipping this release without it'}>
                        <span>
                            <Button
                                size="small"
                                variant="text"
                                color="warning"
                                disabled={!!acknowledgeReason || sending}
                                onClick={() => setConfirming('acknowledge')}
                            >
                                Acknowledge skip
                            </Button>
                        </span>
                    </Tooltip>
                )}
                <Tooltip title={deployReason ?? deployTooltip(row)}>
                    <span>
                        <Button
                            size="small"
                            variant={row.state === 'outstanding' ? 'contained' : 'outlined'}
                            disabled={!!deployReason || sending}
                            onClick={() => (deployNeedsConfirming ? setConfirming('deploy') : deploy())}
                        >
                            {atThisCommit ? 'Redeploy anyway' : 'Deploy'}
                        </Button>
                    </span>
                </Tooltip>
            </Stack>

            {error && !confirming && (
                <Alert severity="error" sx={{ mt: 1 }}>
                    {error}
                </Alert>
            )}

            <Dialog open={confirming === 'deploy'} onClose={() => setConfirming(undefined)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {atThisCommit ? `Redeploy ${row.name}?` : `Deploy ${row.name}?`}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        {deployed && (
                            <DialogContentText>
                                {row.name} is already deployed at this release&apos;s commit
                                {row.deployMarker ? ` (${row.deployMarker.at})` : ''}. Deploying again is harmless and
                                sometimes the point — it re-runs the pipeline — but nothing changes in production.
                            </DialogContentText>
                        )}
                        {upToDate && (
                            <DialogContentText>
                                This release changes nothing in {row.name}, and an earlier release already put this
                                commit in production
                                {row.deployMarker ? ` (${row.deployMarker.at})` : ''}. Deploying it anyway re-runs the
                                pipeline and is recorded against {version} as a deliberate no-change deploy, but
                                nothing changes in production.
                            </DialogContentText>
                        )}
                        {unchanged && !atThisCommit && (
                            <DialogContentText>
                                This release changes nothing in {row.name}. Deploying it anyway exercises the pipeline
                                and is recorded as a deliberate no-change deploy.
                            </DialogContentText>
                        )}
                        {compileOnly && (
                            <DialogContentText>
                                {compileOnlyExplanation(row)} The analysis follows imports file by file and widens
                                wherever it cannot be sure, so deploying anyway is a reasonable call — it is just not
                                one this release is waiting on.
                            </DialogContentText>
                        )}
                        {order && (
                            <Alert severity="warning">
                                <strong>{order.component}</strong> is in group {order.group} and has not been deployed
                                yet. {order.reason} Deploy order is advice, not a rule — carry on if you have a reason
                                to.
                            </Alert>
                        )}
                        {error && <Alert severity="error">{error}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirming(undefined)}>Cancel</Button>
                    <Button variant="contained" disabled={sending} onClick={deploy}>
                        {atThisCommit ? 'Redeploy' : 'Deploy'} {row.name}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirming === 'compile'} onClose={() => setConfirming(undefined)} maxWidth="sm" fullWidth>
                <DialogTitle>Compile {row.name} again?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {row.name} is already compiled at this release&apos;s commit, so a deploy would skip the
                        compile. Compiling again is safe; it just takes the time back.
                    </DialogContentText>
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirming(undefined)}>Cancel</Button>
                    <Button variant="contained" disabled={sending} onClick={compile}>
                        Compile
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirming === 'acknowledge'} onClose={() => setConfirming(undefined)} maxWidth="sm" fullWidth>
                <DialogTitle>Ship {version} without {row.name}?</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <DialogContentText>
                            This release changes {row.name}, and acknowledging the skip is what lets it close without
                            deploying it. The changed code stays out of production until a later release deploys it, so
                            the reason is the only explanation anyone will have.
                        </DialogContentText>
                        <TextField
                            label="Why this component is being left out"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            multiline
                            minRows={2}
                            fullWidth
                            autoFocus
                        />
                        {error && <Alert severity="error">{error}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirming(undefined)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="warning"
                        disabled={sending || reason.trim().length === 0}
                        onClick={() =>
                            void run(INSTRUCTION_KIND.deployAcknowledgeSkip, {
                                component: row.name,
                                reason: reason.trim(),
                            })
                        }
                    >
                        Acknowledge skip
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

/** The operator-facing reason a release action is unavailable in this phase, if any. */
function reasonFor(phase: Phase, kind: InstructionKind): string | undefined {
    return legalInstructions(phase, 'release').find((l) => l.kind === kind)?.reasonIfIllegal;
}

function deployTooltip(row: ComponentRow): string {
    if (row.state === 'deployed') return 'Already at this commit — deploying again re-runs the pipeline';
    if (row.state === 'upToDate') return 'Already live from an earlier release — redeploy to exercise the pipeline';
    if (row.changeKind === 'none') return 'No changes — redeploy to exercise the pipeline';
    if (row.changeKind === 'compile-only') {
        return 'No runtime path from the changed files — redeploy to be safe';
    }
    return `Compile if needed, then deploy ${row.name}`;
}
